export type MemberStatus = 'pending' | 'approved' | 'rejected';
export type MemberRole = 'member' | 'admin';

export interface WeddingMember {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  status: MemberStatus;
  role: MemberRole;
  created_at: string;
  updated_at: string;
}

export type ThemeKey =
  | 'classic-dark'
  | 'white-minimal'
  | 'romantic-pink'
  | 'botanical-green'
  | 'modern-mono'
  | 'terracotta'
  | 'champagne'
  | 'peach'
  | 'linen'
  | 'matcha'
  | 'olive-dark'
  | 'forest-dark'
  | 'wine-dark'
  | 'charcoal-rose';

export interface ThemeDef {
  key: ThemeKey;
  name: string;
  desc: string;
  swatches: [string, string, string];
}

export const THEMES: ThemeDef[] = [
  {
    key: 'classic-dark',
    name: '클래식 다크',
    desc: '딥 브라운과 골드의 클래식한 무드',
    swatches: ['#1a0f08', '#c49a72', '#f0e6da'],
  },
  {
    key: 'white-minimal',
    name: '화이트 미니멀',
    desc: '여백이 넉넉한 담백한 화이트',
    swatches: ['#fbfaf8', '#b08d5f', '#1c1a17'],
  },
  {
    key: 'romantic-pink',
    name: '로맨틱 핑크',
    desc: '부드러운 블러시와 로즈빛',
    swatches: ['#fdf4f4', '#d98a9e', '#3a2a2c'],
  },
  {
    key: 'botanical-green',
    name: '보태니컬 그린',
    desc: '싱그러운 세이지 그린',
    swatches: ['#f4f7f2', '#6f8c5e', '#23301f'],
  },
  {
    key: 'modern-mono',
    name: '모던 모노',
    desc: '블랙 앤 화이트의 모던한 대비',
    swatches: ['#141414', '#e5e5e5', '#f5f5f5'],
  },
  {
    key: 'terracotta',
    name: '테라코타 선셋',
    desc: '따뜻한 흙빛 테라코타와 크림',
    swatches: ['#fbf2ea', '#c26f47', '#472d22'],
  },
  {
    key: 'champagne',
    name: '샴페인 골드',
    desc: '아이보리에 은은하게 빛나는 골드',
    swatches: ['#faf6ed', '#c2a05f', '#3f3624'],
  },
  {
    key: 'peach',
    name: '피치 블라썸',
    desc: '복숭아빛으로 화사한 봄 무드',
    swatches: ['#fff5ef', '#ec8f68', '#4a3026'],
  },
  {
    key: 'linen',
    name: '리넨 베이지',
    desc: '차분한 리넨 천 같은 베이지',
    swatches: ['#f7f3ec', '#a8865a', '#34302a'],
  },
  {
    key: 'matcha',
    name: '말차 크림',
    desc: '부드러운 말차빛 그린과 크림',
    swatches: ['#f2f6ee', '#7d9a4e', '#2b3425'],
  },
  {
    key: 'olive-dark',
    name: '올리브 골드',
    desc: '깊은 올리브와 앤티크 골드',
    swatches: ['#1c1a12', '#cbb26c', '#f1ecdf'],
  },
  {
    key: 'forest-dark',
    name: '딥 포레스트',
    desc: '숲처럼 깊고 차분한 그린',
    swatches: ['#11201a', '#8fb59a', '#e8f0e9'],
  },
  {
    key: 'wine-dark',
    name: '빈티지 와인',
    desc: '와인빛으로 우아한 딥 레드',
    swatches: ['#2a1418', '#cf918b', '#f5e8e6'],
  },
  {
    key: 'charcoal-rose',
    name: '차콜 로즈',
    desc: '차콜 위에 로즈골드 포인트',
    swatches: ['#1a1616', '#d9a08f', '#f2eae8'],
  },
];

export interface PersonInfo {
  name: string;
  parents: string;
  phone: string;
}

export interface BankAccount {
  bank: string;
  number: string;
  holder: string;
}

export interface TransitInfo {
  car: string;
  bus: string;
  subway: string;
}

export interface SignatureFontDef {
  key: string;
  name: string;
  desc: string;
  stack: string;
}

/* 신랑 · 신부 서명에 쓸 수 있는 손글씨 풍 폰트 목록 */
export const SIGNATURE_FONTS: SignatureFontDef[] = [
  {
    key: 'default',
    name: '기본 (테마 서체)',
    desc: '청첩장 테마의 명조 서체',
    stack: '',
  },
  {
    key: 'pen',
    name: '나눔손글씨 펜',
    desc: '또박또박 손으로 쓴 펜글씨',
    stack: '"Nanum Pen Script", var(--font-heading), cursive',
  },
  {
    key: 'brush',
    name: '나눔손글씨 붓',
    desc: '힘 있는 붓글씨 느낌',
    stack: '"Nanum Brush Script", var(--font-heading), cursive',
  },
  {
    key: 'gaegu',
    name: '개구진 손글씨',
    desc: '동글동글 귀여운 필체',
    stack: 'Gaegu, var(--font-heading), cursive',
  },
  {
    key: 'gamja',
    name: '감자꽃 손글씨',
    desc: '정겨운 손편지 느낌',
    stack: '"Gamja Flower", var(--font-heading), cursive',
  },
  {
    key: 'himelody',
    name: '하이멜로디',
    desc: '부드럽게 흐르는 필체',
    stack: '"Hi Melody", var(--font-heading), cursive',
  },
  {
    key: 'yeonsung',
    name: '연성 (붓 흘림)',
    desc: '서예처럼 흘려 쓴 붓 흘림체',
    stack: '"Yeon Sung", var(--font-heading), serif',
  },
  {
    key: 'poorstory',
    name: '푸어스토리',
    desc: '살짝 기울어진 감성 손글씨',
    stack: '"Poor Story", var(--font-heading), cursive',
  },
  {
    key: 'cutefont',
    name: '큐트 손글씨',
    desc: '앳된 분위기의 손글씨',
    stack: '"Cute Font", var(--font-heading), cursive',
  },
];

export function signatureFontStack(key: string): string {
  return SIGNATURE_FONTS.find((f) => f.key === key)?.stack ?? '';
}

/* 신랑 · 신부 메인 이름에 쓸 수 있는 폰트 목록 */
export const NAME_FONTS: SignatureFontDef[] = [
  {
    key: 'default',
    name: '기본 (테마 서체)',
    desc: '청첩장 테마의 명조 서체',
    stack: '',
  },
  {
    key: 'myeongjo',
    name: '나눔명조',
    desc: '단정하고 우아한 명조',
    stack: '"Nanum Myeongjo", var(--font-heading), serif',
  },
  {
    key: 'songmyung',
    name: '송명',
    desc: '붓으로 쓴 듯한 명조',
    stack: '"Song Myung", var(--font-heading), serif',
  },
  {
    key: 'gowun',
    name: '고운바탕',
    desc: '고운 붓글씨 바탕체',
    stack: '"Gowun Batang", var(--font-heading), serif',
  },
  {
    key: 'gugi',
    name: '구기 (전통 흘림)',
    desc: '전통적인 붓 흘림체',
    stack: 'Gugi, var(--font-heading), cursive',
  },
  {
    key: 'dohyeon',
    name: '도현',
    desc: '힘차고 단단한 디스플레이',
    stack: '"Do Hyeon", var(--font-heading), sans-serif',
  },
  {
    key: 'jua',
    name: '주아',
    desc: '동글동글 귀여운 손글씨',
    stack: 'Jua, var(--font-heading), sans-serif',
  },
  {
    key: 'blackhan',
    name: '검은고딕',
    desc: '묵직한 임팩트 고딕',
    stack: '"Black Han Sans", var(--font-heading), sans-serif',
  },
  {
    key: 'pen',
    name: '나눔손글씨 펜',
    desc: '또박또박 손으로 쓴 펜글씨',
    stack: '"Nanum Pen Script", var(--font-heading), cursive',
  },
  {
    key: 'gaegu',
    name: '개구진 손글씨',
    desc: '동글동글 귀여운 필체',
    stack: 'Gaegu, var(--font-heading), cursive',
  },
];

export function nameFontStack(key: string): string {
  return NAME_FONTS.find((f) => f.key === key)?.stack ?? '';
}

export interface GuestbookEntry {
  id: string;
  invitation_id: string;
  name: string;
  side: string;
  message: string;
  created_at: string;
}

export interface RsvpEntry {
  id: string;
  invitation_id: string;
  name: string;
  side: string;
  phone: string;
  attendance: string;
  guests: string;
  meal: string;
  message: string;
  created_at: string;
}

export interface InvitationData {
  groom: PersonInfo;
  bride: PersonInfo;
  date: string;
  time: string;
  venueName: string;
  hall: string;
  address: string;
  mapQuery: string;
  quote: string;
  greeting: string;
  greetingSign: string;
  signatureFont: string;
  nameFont: string;
  groomAccount: BankAccount;
  brideAccount: BankAccount;
  transit: TransitInfo;
  photos: string[];
  musicUrl: string;
}

export interface WeddingInvitation {
  id: string;
  owner_id: string;
  slug: string;
  theme: ThemeKey;
  data: InvitationData;
  published: boolean;
  created_at: string;
  updated_at: string;
}

export const STATUS_LABEL: Record<MemberStatus, string> = {
  pending: '승인 대기',
  approved: '승인 완료',
  rejected: '승인 거절',
};

export function emptyPerson(): PersonInfo {
  return { name: '', parents: '', phone: '' };
}

export function emptyAccount(): BankAccount {
  return { bank: '', number: '', holder: '' };
}

export function emptyTransit(): TransitInfo {
  return { car: '', bus: '', subway: '' };
}

export function emptyInvitationData(): InvitationData {
  return {
    groom: emptyPerson(),
    bride: emptyPerson(),
    date: '',
    time: '',
    venueName: '',
    hall: '',
    address: '',
    mapQuery: '',
    quote: '',
    greeting: '',
    greetingSign: '',
    signatureFont: '',
    nameFont: '',
    groomAccount: emptyAccount(),
    brideAccount: emptyAccount(),
    transit: emptyTransit(),
    photos: [],
    musicUrl: '',
  };
}

function pickString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function pickPerson(raw: unknown, base: PersonInfo): PersonInfo {
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<PersonInfo>;
  return {
    name: pickString(r.name, base.name),
    parents: pickString(r.parents, base.parents),
    phone: pickString(r.phone, base.phone),
  };
}

function pickAccount(raw: unknown, base: BankAccount): BankAccount {
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<BankAccount>;
  return {
    bank: pickString(r.bank, base.bank),
    number: pickString(r.number, base.number),
    holder: pickString(r.holder, base.holder),
  };
}

function pickTransit(raw: unknown, base: TransitInfo): TransitInfo {
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<TransitInfo>;
  return {
    car: pickString(r.car, base.car),
    bus: pickString(r.bus, base.bus),
    subway: pickString(r.subway, base.subway),
  };
}

export function normalizeInvitationData(raw: unknown): InvitationData {
  const base = emptyInvitationData();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<InvitationData>;
  return {
    groom: pickPerson(r.groom, base.groom),
    bride: pickPerson(r.bride, base.bride),
    date: pickString(r.date, base.date),
    time: pickString(r.time, base.time),
    venueName: pickString(r.venueName, base.venueName),
    hall: pickString(r.hall, base.hall),
    address: pickString(r.address, base.address),
    mapQuery: pickString(r.mapQuery, base.mapQuery),
    quote: pickString(r.quote, base.quote),
    greeting: pickString(r.greeting, base.greeting),
    greetingSign: pickString(r.greetingSign, base.greetingSign),
    signatureFont: pickString(r.signatureFont, base.signatureFont),
    nameFont: pickString(r.nameFont, base.nameFont),
    groomAccount: pickAccount(r.groomAccount, base.groomAccount),
    brideAccount: pickAccount(r.brideAccount, base.brideAccount),
    transit: pickTransit(r.transit, base.transit),
    photos: Array.isArray(r.photos)
      ? r.photos.filter((p): p is string => typeof p === 'string' && p.length > 0)
      : [],
    musicUrl: pickString(r.musicUrl, base.musicUrl),
  };
}

export function formatDate(value: string): string {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  } catch {
    return value;
  }
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/* 'YYYY-MM-DD' → '2026년 5월 16일 토요일' */
export function formatKoreanDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-').map((n) => Number(n));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return dateStr;
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  const day = WEEKDAYS[dt.getDay()] ?? '';
  return `${y}년 ${m}월 ${d}일 ${day}요일`;
}

/* 'YYYY-MM-DD' → '2026. 05. 16' */
export function formatShortDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [y, m, d] = parts;
  return `${y}. ${m}. ${d}`;
}

export function themeDef(key: ThemeKey): ThemeDef {
  return THEMES.find((t) => t.key === key) ?? THEMES[0];
}